import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaTag } from '@react-icons/all-files/fa/FaTag';
import { FaBuilding } from '@react-icons/all-files/fa/FaBuilding';
import { FaCheck } from '@react-icons/all-files/fa/FaCheck';
import { FaEraser } from '@react-icons/all-files/fa/FaEraser';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import Breadcrumb from '../../../commons/Breadcrumb';
import PageHeader from '../../../commons/PageHeader';
import PageForm, { FormField } from '../../../commons/PageForm';
import PageAlert, { usePageAlert } from '../../../commons/PageAlert';
import { defaultChatPoster } from '../../../commons/SuperContaChat';
import axios from 'axios';
import { useAppDispatch, useAppSelector } from '../../../store';
import { getEntity, createEntity, updateEntity, reset } from '../../../manager/etiqueta.reducer';
import { EMPRESA_CONFIG_BASE, ETIQUETA_BASE } from './empresa-config-routes';
import {
  EtiquetaFormValues,
  EtiquetaFormVista,
  configuracionToForm,
  etiquetaVisibleFieldNames,
  toEtiquetaPayload,
  validateEtiquetaForm,
  ETIQUETA_NIVELES,
  ETIQUETA_NIVEL_LABELS,
  etiquetaConsolePhaseLines,
  EtiquetaConsoleLine,
} from './etiqueta-form';
import {
  EtiquetaScriptTestReport,
  isEtiquetaGenerateError,
  validarEtiquetaScripts,
} from './etiqueta-superconta';

const empty: EtiquetaFormValues = {
  id: '',
  nombre: '',
  descripcion: '',
  color: '',
  habilitado: 'true',
  nivel: 'NORMAL',
  aplicarScript: 'false',
  script: '',
  scriptTests: '',
  scriptValido: 'false',
};

const allFields: FormField<EtiquetaFormValues>[] = [
  { name: 'id', label: 'ID', readOnly: true, editOnly: true },
  { name: 'nombre', label: 'Nombre', required: true, placeholder: 'Nombre de la etiqueta' },
  { name: 'descripcion', label: 'Descripción', type: 'textarea', rows: 3, placeholder: 'Descripción opcional' },
  {
    name: 'nivel',
    label: 'Nivel',
    type: 'select',
    required: true,
    options: ETIQUETA_NIVELES.map(value => ({ value, label: ETIQUETA_NIVEL_LABELS[value] })),
  },
  { name: 'color', label: 'Color', type: 'color', placeholder: '#3b82f6', hint: 'Elige un color o escribe el hexadecimal (#RRGGBB)' },
  { name: 'habilitado', label: 'Habilitado', type: 'checkbox' },
  { name: 'aplicarScript', label: 'Aplicar script', type: 'checkbox' },
  { name: 'script', label: 'Script', type: 'textarea', rows: 8, placeholder: 'Script de la etiqueta' },
  { name: 'scriptTests', label: 'Test', type: 'textarea', rows: 8, placeholder: 'Script de test' },
  { name: 'scriptValido', label: 'Script válido', type: 'checkbox', readOnly: true, hint: 'Se marca al pasar todos los tests' },
];

async function postEtiquetaScriptTests(script: string, scriptTests: string): Promise<EtiquetaScriptTestReport> {
  const { data } = await axios.post<EtiquetaScriptTestReport>('api/etiquetas/test-script', { script, scriptTests });
  return data ?? { messages: [], results: [], scriptValidated: false };
}

export default function EtiquetaFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const entity = useAppSelector(s => s.etiqueta.entity) as any;
  const updating = useAppSelector(s => s.etiqueta.updating);
  const updateSuccess = useAppSelector(s => s.etiqueta.updateSuccess);
  const errorMessage = useAppSelector(s => s.etiqueta.errorMessage) as string | null;

  const [form, setForm] = useState<EtiquetaFormValues>(empty);
  const [errors, setErrors] = useState<Partial<Record<keyof EtiquetaFormValues, string>>>({});
  const [vista, setVista] = useState<EtiquetaFormVista>('normal');
  const [validating, setValidating] = useState(false);
  const [consoleLines, setConsoleLines] = useState<EtiquetaConsoleLine[]>([]);
  const { alert, showToast } = usePageAlert();

  const fields = useMemo(() => {
    const visible = new Set(etiquetaVisibleFieldNames(vista));
    return allFields.filter(field => visible.has(String(field.name)));
  }, [vista]);

  useEffect(() => {
    if (!isNew) {
      dispatch(getEntity(id!));
    } else {
      dispatch(reset());
      setForm(empty);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isNew && entity?.id) {
      setForm({
        id: String(entity.id),
        nombre: entity.nombre ?? '',
        descripcion: entity.descripcion ?? '',
        color: entity.color ?? '',
        habilitado: String(entity.habilitado ?? true),
        nivel: entity.nivel ?? 'NORMAL',
        ...configuracionToForm(entity.configuracion),
      });
    }
  }, [entity]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      navigate(ETIQUETA_BASE, {
        state: {
          successMsg: isNew
            ? 'Etiqueta creada correctamente.'
            : 'Etiqueta actualizada correctamente.',
        },
      });
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!errorMessage) {
      return;
    }
    showToast('error', errorMessage);
    setConsoleLines(prev => [...prev, { kind: 'error', text: errorMessage }]);
  }, [errorMessage, showToast]);

  const handleFieldChange = (name: keyof EtiquetaFormValues, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleValidar = async () => {
    setConsoleLines(etiquetaConsolePhaseLines('generando'));
    setValidating(true);
    try {
      const result = await validarEtiquetaScripts(
        defaultChatPoster,
        form.descripcion,
        postEtiquetaScriptTests,
        scripts => {
          setForm(prev => ({
            ...prev,
            script: scripts.script,
            scriptTests: scripts.scriptTests,
            scriptValido: 'false',
          }));
          setConsoleLines(prev => [...prev, ...etiquetaConsolePhaseLines('ejecutando')]);
        },
      );
      setForm(prev => ({
        ...prev,
        scriptValido: String(result.scriptValido),
      }));
      setConsoleLines(prev => [...prev, ...etiquetaConsolePhaseLines('resultado', { report: result.report })]);
      if (result.scriptValido) {
        showToast('success', 'Se completaron el script y todos los tests pasaron.');
      } else {
        showToast('warning', 'Se completaron los scripts, pero hay tests que no pasaron.');
      }
    } catch (err) {
      const message = err instanceof Error && err.message.trim()
        ? err.message
        : 'No se pudieron completar los scripts.';
      const phase = isEtiquetaGenerateError(err) ? 'errorGenerar' : 'errorTesting';
      setConsoleLines(prev => [...prev, ...etiquetaConsolePhaseLines(phase, { detail: message })]);
      showToast('error', message);
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validateEtiquetaForm(form);
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    const payload = toEtiquetaPayload(form);
    if (isNew) {
      dispatch(createEntity(payload));
    } else {
      dispatch(updateEntity(payload));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Empresa', to: EMPRESA_CONFIG_BASE, icon: <FaBuilding /> },
        { label: 'Etiquetas', to: ETIQUETA_BASE, icon: <FaTag /> },
        { label: isNew ? 'Nueva etiqueta' : `Editar etiqueta #${id}` },
      ]} />

      <PageHeader
        icon={<FaTag />}
        iconColor="#ec4899"
        title={isNew ? 'Nueva etiqueta' : 'Editar etiqueta'}
        subtitle={isNew
          ? 'Completa los campos para crear una etiqueta'
          : `Modificando etiqueta #${id}`}
      />

      <div style={s.vistaToggle} role="group" aria-label="Vista del formulario">
        {(['normal', 'avanzada'] as EtiquetaFormVista[]).map(option => (
          <button
            key={option}
            type="button"
            style={{
              ...s.vistaBtn,
              ...(vista === option ? s.vistaBtnActive : {}),
            }}
            aria-pressed={vista === option}
            onClick={() => setVista(option)}
          >
            {option === 'normal' ? 'Normal' : 'Avanzada'}
          </button>
        ))}
      </div>

      {alert && <PageAlert alert={alert} />}

      <div style={vista === 'avanzada' ? s.split : undefined}>
        <div style={vista === 'avanzada' ? s.splitForm : undefined}>
          <PageForm
            fields={fields}
            values={form}
            errors={errors}
            isNew={isNew}
            saving={updating || validating}
            onFieldChange={handleFieldChange}
            onSubmit={handleSubmit}
            onCancel={() => navigate(ETIQUETA_BASE)}
            accentColor="#ec4899"
          />
        </div>
        {vista === 'avanzada' ? (
          <aside style={s.splitPanel}>
            <EtiquetaTestConsole
              lines={consoleLines}
              onValidar={handleValidar}
              onClear={() => setConsoleLines([])}
              validating={validating}
              disabled={updating || validating}
            />
          </aside>
        ) : null}
      </div>
    </div>
  );
}

function EtiquetaTestConsole({
  lines,
  onValidar,
  onClear,
  validating,
  disabled,
}: {
  lines: EtiquetaConsoleLine[];
  onValidar: () => void;
  onClear: () => void;
  validating?: boolean;
  disabled?: boolean;
}) {
  return (
    <div style={s.consoleWrap} aria-label="Consola de tests">
      <div style={s.consoleToolbar}>
        <div style={s.consoleTitle}>Consola</div>
        <div style={s.consoleActions}>
          <button
            type="button"
            style={s.consoleValidar}
            onClick={onValidar}
            disabled={disabled}
          >
            <FaCheck style={{ marginRight: 6 }} />
            {validating ? 'Validando…' : 'Validar'}
          </button>
          <button
            type="button"
            style={s.consoleClear}
            onClick={onClear}
            disabled={disabled}
          >
            <FaEraser style={{ marginRight: 6 }} />
            Limpiar
          </button>
        </div>
      </div>
      <div style={s.console}>
        {lines.length === 0 ? (
          <div style={s.consoleIdle}>$ listo</div>
        ) : (
          lines.map((line, index) => (
            <div
              key={`${line.kind}-${index}`}
              style={{
                ...s.consoleLine,
                color: consoleColor[line.kind],
                fontWeight: line.kind === 'phase' ? 700 : undefined,
              }}
            >
              {line.text}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const consoleColor: Record<string, string> = {
  ok: '#4ade80',
  fail: '#f87171',
  error: '#f87171',
  info: '#93c5fd',
  phase: '#fbbf24',
};

const s: Record<string, React.CSSProperties> = {
  vistaToggle: {
    display: 'inline-flex',
    alignSelf: 'flex-start',
    padding: 3,
    gap: 4,
    background: '#f1f5f9',
    borderRadius: 10,
  },
  vistaBtn: {
    border: 'none',
    background: 'transparent',
    padding: '7px 14px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    color: '#64748b',
    cursor: 'pointer',
  },
  vistaBtnActive: {
    background: '#fff',
    color: '#ec4899',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
  },
  split: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 16,
    flexWrap: 'wrap',
  },
  splitForm: {
    flex: '1 1 460px',
    minWidth: 0,
  },
  splitPanel: {
    flex: '1 1 340px',
    minWidth: 280,
    maxWidth: 560,
    position: 'sticky',
    top: 16,
    alignSelf: 'stretch',
  },
  consoleWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    height: '100%',
    minHeight: 420,
    padding: '16px 16px 18px',
    background: '#0b1220',
    borderRadius: 14,
    border: '1px solid #1e293b',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.12)',
  },
  consoleToolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  consoleTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: '#94a3b8',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  consoleActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  consoleValidar: {
    display: 'inline-flex',
    alignItems: 'center',
    border: '1px solid #ec4899',
    background: '#be185d',
    color: '#fff',
    borderRadius: 7,
    padding: '5px 10px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  consoleClear: {
    display: 'inline-flex',
    alignItems: 'center',
    border: '1px solid #334155',
    background: '#111827',
    color: '#cbd5e1',
    borderRadius: 7,
    padding: '5px 10px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  console: {
    background: '#05070a',
    color: '#e2e8f0',
    borderRadius: 10,
    border: '1px solid #1e293b',
    flex: 1,
    minHeight: 280,
    overflow: 'auto',
    padding: '12px 14px',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontSize: 12,
    lineHeight: 1.55,
    boxShadow: 'inset 0 0 0 1px rgba(15, 23, 42, 0.6)',
  },
  consoleIdle: {
    color: '#64748b',
  },
  consoleLine: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
};
